// @ts-nocheck — modulo UI (DOM-heavy). Logica pura testabile in area-collectors.js
// ============================================================
// Controllo Area — cruscotto direzionale del responsabile (G1)
// Aggrega: alert a soglia (F5), carico per persona (F2), copertura accessi
// (F4), pipeline rinnovi contratti (F3), grafici (G3) e trend storico (F6).
// NON gestisce ticket: solo supervisione di clienti/accessi/contratti/carico.
// ============================================================

import { jsPDF } from 'jspdf';

const ALERT_ICONS = {
  critical:
    '<svg class="aa-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round"><path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>',
  warning:
    '<svg class="aa-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>',
  info: '<svg class="aa-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>'
};

function _areaThresholds() {
  const p = (typeof getUserPrefs === 'function' ? getUserPrefs() : {}) || {};
  return {
    contractDays: Number(p.area_contract_days != null ? p.area_contract_days : 30),
    stuckDays: Number(p.area_stuck_days != null ? p.area_stuck_days : 14)
  };
}

function _fmtWhen(g) {
  if (g == null) return '';
  if (g < 0) return `scaduto ${-g}gg fa`;
  if (g === 0) return 'oggi';
  if (g === 1) return 'domani';
  return `tra ${g}gg`;
}

function renderAreaAlerts(targetId, alerts) {
  const el = document.getElementById(targetId);
  if (!el) return;
  if (!alerts || !alerts.length) {
    el.innerHTML = `<div class="area-alerts-empty">
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--success)" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6 9 17l-5-5"/></svg>
      Nessun avviso: situazione dell'area sotto controllo.</div>`;
    return;
  }
  el.innerHTML = alerts
    .map(
      (a) => `
    <div class="area-alert lvl-${a.level}" data-view="${escapeHtml(a.view || 'controllo')}" role="button" tabindex="0">
      ${ALERT_ICONS[a.level] || ALERT_ICONS.info}
      <div class="aa-body">
        <div class="aa-title">${escapeHtml(a.title)}</div>
        <div class="aa-detail">${escapeHtml(a.detail || '')}</div>
      </div>
      <div class="aa-count">${a.count}</div>
    </div>`
    )
    .join('');
  // La navigazione è già gestita dal listener globale [data-view] del router.
}

function _coverageRingColor(pct) {
  if (pct >= 90) return 'var(--success)';
  if (pct >= 70) return 'var(--warning)';
  return 'var(--danger)';
}

async function renderControlloArea(rootId = 'controlloRoot') {
  const root = typeof rootId === 'string' ? document.getElementById(rootId) : rootId;
  if (!root) return;
  if (typeof isAdmin === 'function' && !isAdmin()) {
    root.innerHTML =
      '<div class="card" style="padding:20px;color:var(--text-muted)">Sezione riservata agli amministratori.</div>';
    return;
  }

  const clients = (typeof state !== 'undefined' && state.clients) || [];
  const th = _areaThresholds();
  const alerts = computeAlerts(clients, th);
  const workload = computeWorkload(clients);
  const coverage = computeCoverage(clients);
  const renewals = computeRenewals(clients);

  const orphan = clients.filter((c) => !c.cpl || !String(c.cpl).trim()).length;
  const upcoming90 = renewals.upcoming.filter((r) => r.giorni >= 0 && r.giorni <= 90);
  const ore90 = upcoming90.reduce((s, r) => s + (r.ore || 0), 0) + renewals.buckets.overdue.ore;

  // CPL distribution per il grafico
  const cplCount = {};
  clients.forEach((c) => {
    const p = (c.cpl || 'Non assegnato').trim() || 'Non assegnato';
    cplCount[p] = (cplCount[p] || 0) + 1;
  });
  // Stato accessi nell'area
  const statoCount = {};
  clients.forEach((c) =>
    Object.values(c.accessi || {}).forEach((s) => {
      const k = (s || '').trim();
      if (!k) return;
      statoCount[k] = (statoCount[k] || 0) + 1;
    })
  );

  const maxClienti = Math.max(1, ...workload.map((w) => w.clienti));
  const workloadTop = workload.slice(0, 12);

  const bucket = (b, cls, label) => `
    <div class="renew-bucket ${cls}">
      <div class="rb-count">${b.count}</div>
      <div class="rb-label">${label}</div>
      <div class="rb-hours">${b.ore ? b.ore + ' ore' : '—'}</div>
    </div>`;

  root.innerHTML = `
    <div class="card" style="display:flex;justify-content:space-between;align-items:center;gap:12px;flex-wrap:wrap">
      <div>
        <h3 style="margin:0">Controllo area Ma.Se.</h3>
        <span class="muted" style="font-size:12px">Vista direzionale aggregata · ${clients.length} clienti</span>
      </div>
      <button class="btn btn-sm" id="btnAreaPdf" title="Esporta la situazione area in PDF (una pagina)">
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
        Esporta situazione (PDF)
      </button>
    </div>

    <div class="area-alerts" id="areaAlerts"></div>

    <div class="kpi-grid kpi-grid-5">
      <div class="kpi kpi-featured"><div class="kpi-label">Clienti in gestione</div><div class="kpi-value">${clients.length}</div><div class="kpi-trend">Area Managed Services</div></div>
      <div class="kpi"><div class="kpi-label">Copertura accessi</div><div class="kpi-value ${coverage.pct >= 90 ? 'kpi-ok' : coverage.pct >= 70 ? 'kpi-warn' : 'kpi-danger'}">${coverage.pct}%</div><div class="kpi-trend">${coverage.active}/${coverage.assigned} attivi</div></div>
      <div class="kpi"><div class="kpi-label">Rinnovi ≤ 90gg</div><div class="kpi-value kpi-warn">${upcoming90.length}</div><div class="kpi-trend">${renewals.buckets.overdue.count} già scaduti</div></div>
      <div class="kpi"><div class="kpi-label">Clienti senza CPL</div><div class="kpi-value ${orphan ? 'kpi-danger' : 'kpi-ok'}">${orphan}</div><div class="kpi-trend">da assegnare</div></div>
      <div class="kpi"><div class="kpi-label">Ore in scadenza ≤90gg</div><div class="kpi-value">${ore90 || 0}</div><div class="kpi-trend">monte ore contratti</div></div>
    </div>

    <h3 class="area-section-title">Pipeline rinnovi contratti</h3>
    <div class="card">
      <div class="renew-pipeline">
        ${bucket(renewals.buckets.overdue, 'b-overdue', 'Scaduti')}
        ${bucket(renewals.buckets.d30, 'b-30', 'Entro 30 giorni')}
        ${bucket(renewals.buckets.d60, 'b-60', '31–60 giorni')}
        ${bucket(renewals.buckets.d90, 'b-90', '61–90 giorni')}
      </div>
      <div class="renew-list" id="areaRenewList">
        ${
          [...renewals.buckets.overdue.rows, ...upcoming90]
            .slice(0, 14)
            .map(
              (r) => `
          <div class="renew-row" data-cliente="${escapeHtml(r.cliente)}" role="button" tabindex="0">
            <div><strong>${escapeHtml(r.cliente)}</strong> <span class="rr-meta">${escapeHtml(r.tipo || r.numero || '')}</span></div>
            <div class="rr-meta">${escapeHtml(r.cpl || '—')}${r.ore ? ' · ' + r.ore + 'h' : ''}</div>
            <div class="rr-when ${r.giorni < 0 ? 'dl-danger' : r.giorni <= 30 ? 'dl-warn' : ''}">${escapeHtml(fmtDate(r.fine))} · ${_fmtWhen(r.giorni)}</div>
          </div>`
            )
            .join('') ||
          '<div class="trend-empty">Nessun contratto con data di fine valorizzata.</div>'
        }
      </div>
    </div>

    <div class="grid-2">
      <div class="card">
        <div class="card-header"><h3>Carico di lavoro per persona</h3><span class="muted">clienti seguiti · azioni aperte</span></div>
        <div class="card-body">
          <table class="workload-table">
            <thead><tr><th>Persona</th><th>Clienti</th><th>CPL</th><th>PM</th><th>Tecnico</th><th>Azioni</th></tr></thead>
            <tbody>
              ${
                workloadTop
                  .map((w) => {
                    const pct = Math.round((w.clienti / maxClienti) * 100);
                    const cls = pct >= 80 ? 'hot' : pct >= 55 ? 'warm' : '';
                    return `<tr>
                  <td class="workload-name">${escapeHtml(w.name)}</td>
                  <td><div class="workload-bar-wrap"><div class="workload-bar-track"><div class="workload-bar-fill ${cls}" style="width:${pct}%"></div></div><span class="workload-num">${w.clienti}</span></div></td>
                  <td>${w.comeCpl || '—'}</td>
                  <td>${w.comePm || '—'}</td>
                  <td>${w.comeTecnico || '—'}</td>
                  <td><span class="workload-pill ${w.azioniAperte ? '' : 'zero'}">${w.azioniAperte}</span></td>
                </tr>`;
                  })
                  .join('') ||
                '<tr><td colspan="6" class="muted" style="padding:14px">Nessuna persona assegnata.</td></tr>'
              }
            </tbody>
          </table>
          ${workload.length > workloadTop.length ? `<p class="muted" style="margin-top:8px">+${workload.length - workloadTop.length} altre persone</p>` : ''}
        </div>
      </div>

      <div class="card">
        <div class="card-header"><h3>Copertura accessi</h3><span class="muted">clienti con provisioning incompleto</span></div>
        <div class="card-body">
          <div class="coverage-grid" style="margin-bottom:14px">
            <div class="coverage-card">
              <div class="coverage-ring" style="--pct:${coverage.pct};--ring:${_coverageRingColor(coverage.pct)}"><span>${coverage.pct}%</span></div>
              <div class="coverage-label">Copertura area</div>
              <div class="coverage-sub">${coverage.active} attivi su ${coverage.assigned} assegnati</div>
            </div>
            <div class="coverage-card">
              <div class="coverage-ring" style="--pct:${clients.length ? Math.round(((clients.length - coverage.gaps.length) / clients.length) * 100) : 100};--ring:var(--primary)"><span>${coverage.gaps.length}</span></div>
              <div class="coverage-label">Clienti con gap</div>
              <div class="coverage-sub">accessi da completare</div>
            </div>
          </div>
          <table class="workload-table" id="areaCoverageGaps">
            <thead><tr><th>Cliente</th><th>CPL</th><th>Da completare</th><th>%</th></tr></thead>
            <tbody>
              ${
                coverage.gaps
                  .slice(0, 10)
                  .map(
                    (
                      g
                    ) => `<tr data-cliente="${escapeHtml(g.cliente)}" role="button" tabindex="0" style="cursor:pointer">
                  <td class="workload-name">${escapeHtml(g.cliente)}</td>
                  <td>${escapeHtml(g.cpl || '—')}</td>
                  <td><span class="workload-pill">${g.pending}</span></td>
                  <td class="workload-num">${g.pct}%</td>
                </tr>`
                  )
                  .join('') ||
                '<tr><td colspan="4" class="muted" style="padding:14px">Tutti i clienti hanno la copertura completa.</td></tr>'
              }
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <div class="grid-2">
      <div class="card"><div class="card-header"><h3>Clienti per Client Partner & Lead</h3></div><div class="card-body" id="areaChartCpl"></div></div>
      <div class="card"><div class="card-header"><h3>Stato accessi nell'area</h3></div><div class="card-body" id="areaChartStato"></div></div>
    </div>

    <h3 class="area-section-title">Andamento storico</h3>
    <div class="card">
      <div class="card-header"><h3>Evoluzione area</h3><span class="muted" id="areaTrendDelta"></span></div>
      <div class="card-body">
        <div class="trend-card"><div class="chart-container"><canvas id="areaTrendChart"></canvas></div></div>
        <p class="muted" id="areaTrendNote" style="font-size:11px;margin-top:8px"></p>
      </div>
    </div>
  `;

  renderAreaAlerts('areaAlerts', alerts);

  // Grafici (riusa l'helper della dashboard)
  if (typeof renderChartIn === 'function') {
    renderChartIn('areaChartCpl', cplCount, 'cv-area-cpl');
    renderChartIn('areaChartStato', statoCount, 'cv-area-stato');
  }

  // Handlers riga: rinnovi e gap → aprono la scheda cliente
  root
    .querySelectorAll('#areaRenewList .renew-row, #areaCoverageGaps tr[data-cliente]')
    .forEach((el) =>
      el.addEventListener('click', () => {
        if (typeof openModal === 'function') openModal(el.dataset.cliente, 'contratto');
      })
    );
  const pdfBtn = root.querySelector('#btnAreaPdf');
  if (pdfBtn) pdfBtn.addEventListener('click', () => exportAreaSituazionePDF());

  // Trend storico (async, dagli snapshot di backup)
  try {
    const trends = (typeof loadAreaTrends === 'function' && (await loadAreaTrends())) || {
      points: []
    };
    const note = document.getElementById('areaTrendNote');
    const deltaEl = document.getElementById('areaTrendDelta');
    if (!trends.points || trends.points.length < 2) {
      if (note)
        note.textContent =
          'Il grafico si popola automaticamente con gli snapshot di backup giornalieri (servono almeno 2 giorni di dati).';
      return;
    }
    if (typeof renderChartLine === 'function') {
      renderChartLine('areaTrendChart', {
        labels: trends.points.map((p) => fmtDate(p.date)),
        series: [
          { label: 'Clienti', data: trends.points.map((p) => p.clienti) },
          { label: 'Accessi', data: trends.points.map((p) => p.accessi) },
          { label: 'Contratti', data: trends.points.map((p) => p.contratti) }
        ]
      });
    }
    if (deltaEl && typeof trendDelta === 'function') {
      const d = trendDelta(trends.points, 'clienti');
      const cls = d > 0 ? 'up' : d < 0 ? 'down' : 'flat';
      const seg = trends.points.length;
      deltaEl.innerHTML = `<span class="trend-delta ${cls}">${d > 0 ? '+' : ''}${d} clienti</span> su ${seg} rilevazioni`;
    }
    if (note) note.textContent = `Basato su ${trends.points.length} snapshot di backup.`;
  } catch (e) {
    console.warn('[controllo-area] trend non disponibile', e);
  }
}

// ===== Export PDF "situazione area" (G4) — una pagina riepilogativa =====
function exportAreaSituazionePDF() {
  if (typeof jsPDF !== 'function') {
    toast('Libreria PDF non disponibile');
    return;
  }
  const clients = (typeof state !== 'undefined' && state.clients) || [];
  const th = _areaThresholds();
  const alerts = computeAlerts(clients, th);
  const coverage = computeCoverage(clients);
  const renewals = computeRenewals(clients);
  const workload = computeWorkload(clients).slice(0, 10);
  const orphan = clients.filter((c) => !c.cpl || !String(c.cpl).trim()).length;

  const doc = new jsPDF({ unit: 'pt', format: 'a4' });
  const W = doc.internal.pageSize.getWidth();
  let y = 48;
  doc.setFontSize(18);
  doc.text('Portale Ma.Se. — Situazione area', 40, y);
  doc.setFontSize(10);
  doc.setTextColor(120);
  y += 16;
  doc.text(`Generato il ${fmtDateTime(new Date().toISOString())}`, 40, y);
  doc.setTextColor(20);

  y += 28;
  doc.setFontSize(12);
  doc.text('Indicatori chiave', 40, y);
  doc.setFontSize(10);
  const kpis = [
    ['Clienti in gestione', String(clients.length)],
    ['Copertura accessi', `${coverage.pct}% (${coverage.active}/${coverage.assigned})`],
    ['Contratti scaduti', String(renewals.buckets.overdue.count)],
    [
      'Rinnovi entro 90gg',
      String(renewals.upcoming.filter((r) => r.giorni >= 0 && r.giorni <= 90).length)
    ],
    ['Clienti senza CPL', String(orphan)]
  ];
  kpis.forEach(([k, v]) => {
    y += 16;
    doc.setTextColor(110);
    doc.text(k, 48, y);
    doc.setTextColor(20);
    doc.text(v, W - 48, y, { align: 'right' });
  });

  y += 30;
  doc.setFontSize(12);
  doc.text('Avvisi', 40, y);
  doc.setFontSize(10);
  if (!alerts.length) {
    y += 16;
    doc.text('Nessun avviso attivo.', 48, y);
  } else {
    alerts.forEach((a) => {
      y += 16;
      doc.text(`• [${a.level.toUpperCase()}] ${a.title}: ${a.count}`, 48, y);
    });
  }

  y += 30;
  doc.setFontSize(12);
  doc.text('Carico per persona (top 10)', 40, y);
  doc.setFontSize(10);
  workload.forEach((w) => {
    y += 15;
    if (y > 780) {
      doc.addPage();
      y = 48;
    }
    doc.setTextColor(110);
    doc.text(w.name, 48, y);
    doc.setTextColor(20);
    doc.text(`${w.clienti} clienti · ${w.azioniAperte} azioni aperte`, W - 48, y, {
      align: 'right'
    });
  });

  const ts = new Date().toISOString().slice(0, 10);
  doc.save(`portale-mase-situazione-area-${ts}.pdf`);
  toast('PDF situazione area esportato');
}

// S3: adozione eventBus — quando cambia un accesso (es. drag&drop nel Kanban)
// il cruscotto, se montato, si ricalcola in push. Niente doppio render: nessun
// altro punto rerenderizza Controllo area su questo evento.
if (globalThis.bus) {
  globalThis.bus.on('accessi:changed', () => {
    if (document.getElementById('controlloRoot')) renderControlloArea('controlloRoot');
  });
}

Object.assign(globalThis, {
  renderControlloArea,
  renderAreaAlerts,
  exportAreaSituazionePDF
});
export {};
